import CloseRounded from '@mui/icons-material/CloseRounded';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

import {
  ImportPromiseRibbonMessage,
  RibbonPhoneLinks,
  RibbonSocialLinks,
} from '@/components/molecules';
import { importPromise } from '@/content/importPromise';
import { PROMISE_BANNER_ATTR, useImportPromiseBanner } from '@/hooks/useImportPromiseBanner';
import { useSheenOnEnter } from '@/hooks/useSheenOnEnter';
import { PROMISE_BAR_HEIGHT_VAR } from '@/theme';

const { banner } = importPromise;

/** One line of text plus breathing room. Published as a variable; see below. */
export const PROMISE_BAR_HEIGHT = 40;

/**
 * The extra row the ribbon grows below md to hold the social links.
 *
 * From md up the links live in the utility strip and move into the ribbon's
 * control cluster when that strip folds away, so they cost no height at all.
 * Below md the strip does not exist, so there is nowhere for them to move
 * *from* — and there is no room beside the message either: the promise alone
 * is ~310px of a 344px row on a 360px phone and already truncates. So the
 * ribbon gives them a row rather than squeezing them into one, and states
 * what that costs here instead of burying it in a breakpoint.
 *
 * 32px holds an 18px glyph on a 30px tap target. Short of the theme's 44px,
 * which is the same exception the controls above make, but a good deal less
 * short than the 28px they settle for — that number is defended by md-up
 * pointers and this row is the one place with neither.
 */
export const PROMISE_SOCIAL_ROW_HEIGHT = 32;

/** What the chrome actually costs below md, where both rows are present. */
export const PROMISE_BAR_HEIGHT_COMPACT = PROMISE_BAR_HEIGHT + PROMISE_SOCIAL_ROW_HEIGHT;

/**
 * The promise's colour while the ribbon floats over the home hero.
 *
 * A fixed hex and not `primary.main`, because the hero is the one surface on
 * this site that is dark in *both* colour schemes — it hard-codes `#0A0A0C`.
 * A palette reference would resolve to the light scheme's `#C53D49` for most
 * visitors, which is 4.14:1 on that black and fails AA outright.
 *
 * `#F0929B` is the dark scheme's `primaryHover`, already in the token set, and
 * measures 8.75:1 on `#0A0A0C` — AA with room to spare at the ribbon's 13px.
 * Light red rather than the nav's white on purpose: floating with no bar
 * behind it, the promise would otherwise read as a fourth row of navigation.
 */
const FLOATING_INK = '#F0929B';

/**
 * The ribbon's height, as a variable the rest of the page can read.
 *
 * Three things have to agree on how tall the chrome is: the ribbon itself,
 * the spacer in `SiteLayout` that pushes content clear of the fixed header,
 * and the `scrollMarginTop` on everything a `#fragment` points at. Hard-coding
 * 40 in all three is three places to forget when the ribbon is dismissed and
 * its height becomes zero, so the number is stated once, here, and the other
 * two add `var(--kbx-promise-bar-h)`.
 *
 * The dismissed rule is keyed off an attribute on `<html>` that the pre-paint
 * script in `root.tsx` sets before the first frame — so a returning visitor
 * who closed the ribbon never sees it render and never sees the layout
 * settle. `display: none` as well as the zeroed variable, because a collapsed
 * bar whose link is still in the tab order is worse than no bar.
 *
 * The number is breakpoint-dependent since the ribbon grew its second row,
 * and it is declared mobile-first for a reason: the variable is read by
 * `SiteLayout`'s spacer, by every `scrollMarginTop`, and by the hero's top
 * padding, none of which can branch on a width themselves. A media query
 * here is the only place the two heights can be reconciled once.
 *
 * The dismissed rule needs no query of its own. `html[data-…]` outranks
 * `:root` on specificity, so zero wins at every width regardless of order.
 */
function RibbonMetrics() {
  return (
    <GlobalStyles
      styles={(theme) => ({
        ':root': { [PROMISE_BAR_HEIGHT_VAR]: `${PROMISE_BAR_HEIGHT_COMPACT}px` },
        [theme.breakpoints.up('md')]: {
          ':root': { [PROMISE_BAR_HEIGHT_VAR]: `${PROMISE_BAR_HEIGHT}px` },
        },
        [`html[${PROMISE_BANNER_ATTR}="dismissed"]`]: {
          [PROMISE_BAR_HEIGHT_VAR]: '0px',
          '& .kbx-promise-ribbon': { display: 'none' },
        },
      })}
    />
  );
}

/**
 * The site-wide import promise as a fixed ribbon above the header (§27b).
 *
 * Rendered as the first row *inside* the header's `AppBar` rather than as a
 * second fixed element above it. The AppBar is already the site's fixed
 * chrome, so joining it means the ribbon inherits the one `position: fixed`
 * and the one z-index that exist, and there is no second element whose `top`
 * has to be kept in step. It also solves the home page for free: the AppBar
 * there is transparent over the hero, and a child with its own solid
 * background stays solid regardless.
 *
 * Brand red with its contrast text, which is the one pairing on this site
 * already verified at both schemes — 5.08:1 in light, 6.27:1 in dark (see
 * `scripts/verify-a11y.ts`). The ribbon never collapses on scroll, unlike the
 * phone-and-socials strip below it: that strip is a convenience the reader
 * can scroll back for, and this is the sentence they are meant to leave with.
 */
type Props = {
  /**
   * Take in the utility strip's contents, because the strip has folded away.
   *
   * Both halves of it, not just one: the numbers land in the ribbon's first
   * column and the socials in its third, which is what the strip itself does
   * and what finally gives that first column something to hold. It was named
   * `showSocials` while the numbers had nowhere to go — the rename is the
   * honest description of a handover that is now complete.
   *
   * The two halves land at different widths, because they cost different
   * amounts: the socials from md, the numbers only from lg, where the row
   * measures wide enough to seat them without the promise giving ground (see
   * the column itself). Below md the prop does nothing at all — there is no
   * strip to fold, the socials live on a permanent row of their own, and the
   * numbers are not in the ribbon on a phone.
   *
   * Passed from `SiteHeader` rather than read from a scroll hook here: that
   * component already owns the one `useScrollTrigger` the header runs on, and
   * a second observer watching the same scroll to reach the same conclusion
   * is how the two rows end up disagreeing about whether the page has moved.
   */
  showUtilities?: boolean;
  /**
   * Paint the bar, or let the hero through it.
   *
   * `false` only on a route whose header is transparent — today just the home
   * page. The value is `SiteHeader`'s own `solid`, so the ribbon and the bar
   * below it change together off one `useScrollTrigger`: transparent over the
   * hero, brand red from the moment the header frosts. Defaulting to `true`
   * keeps every other page exactly as it was.
   */
  solid?: boolean;
};

export function ImportPromiseRibbon({ showUtilities = false, solid = true }: Props) {
  const { dismiss } = useImportPromiseBanner();
  const { ref, play } = useSheenOnEnter<HTMLDivElement>();

  return (
    <>
      <RibbonMetrics />
      <Box
        ref={ref}
        className="kbx-promise-ribbon"
        component="aside"
        aria-label={banner.label}
        sx={{
          // Three columns so the promise stays centred on the bar rather than
          // on whatever space the controls leave it. The outer two are equal
          // and empty-able, so the message does not shift when the numbers and
          // the social links arrive and leave.
          //
          // Centred *while there is room to be*, which is what the `auto`
          // minimums buy. Under `minmax(0, 1fr)` the outer tracks are always
          // exactly equal, so they split the leftover space whether or not
          // that is enough for what is in them: at 900px the controls measured
          // 171px against an 117px track and the other 54px was drawn straight
          // over the end of the promise. An `auto` minimum floors each track at
          // its own content, so the pair stays equal — and the message stays
          // centred — right up to the width where equality would mean a
          // collision, and from there the message gives ground instead.
          //
          // The centre column keeps a zero minimum for the same reason in
          // reverse. It is the one thing on the bar that can afford to lose a
          // few words; with all three tracks floored, a width narrow enough to
          // exhaust the row would have nowhere to take the space from and the
          // whole ribbon would overhang the viewport — and because the bar is
          // `position: fixed`, it would do so with no scrollbar to show for it.
          //
          // Two rows below md, one from md up. The rows carry the height
          // rather than a `height` of their own, so the box cannot drift out
          // of step with the variable `RibbonMetrics` publishes — both are
          // built from the same two constants.
          display: 'grid',
          gridTemplateColumns: 'minmax(auto, 1fr) minmax(0, auto) minmax(auto, 1fr)',
          // A gutter the columns cannot spend. The floors above stop the
          // controls being drawn over the promise, but touching is not the
          // same as separated: at 900px the message ended exactly on the
          // first social icon with nothing between them. 12px is the width
          // at which the three groups read as three.
          columnGap: 3,
          gridTemplateRows: {
            xs: `${PROMISE_BAR_HEIGHT}px ${PROMISE_SOCIAL_ROW_HEIGHT}px`,
            md: `${PROMISE_BAR_HEIGHT}px`,
          },
          alignItems: 'center',
          px: { xs: 2, md: 6 },
          // Floating, the ribbon is chrome over the hero rather than a band
          // above it; solid, it is the brand bar every other page carries.
          // The AppBar behind this row fades its own background on the same
          // trigger, so both are stated outright rather than inherited.
          bgcolor: solid ? 'primary.main' : 'transparent',
          color: solid ? 'primary.contrastText' : FLOATING_INK,
          backgroundImage: 'none',
          // Matches the AppBar's own fade, so the two surfaces arrive
          // together instead of the ribbon snapping a frame ahead of it.
          transition: (t) =>
            t.transitions.create(['background-color', 'color'], {
              duration: t.transitions.duration.short,
            }),
        }}
      >
        {/*
          Column one: the numbers, once the strip below has folded them up
          here, and an empty cell holding the column open until it does.

          The cell is rendered either way rather than conditionally, because
          its job when empty is the job it had before there was anything to
          put in it — balancing the controls in column three so the promise is
          centred on the bar rather than on the space they leave. Mounting and
          unmounting it would make the message jump on every scroll.

          **lg and up, not md**, and that is measured rather than preferred.
          The track floors at its own content now, so anything put here at md
          is taken out of the row rather than out of thin air — and at 900px
          the row has none to give: the promise and its proof clause measure
          617px and the controls 171px against 852px of usable bar, which
          leaves 64px for a number that needs 118. The strip's own numbers are
          no further than a scroll up at that width.

          `overflow: hidden` is a backstop, not a layout tool. The floor means
          the column is sized to fit what is in it, but a longer number in
          `company.ts` than the widths here were measured against must clip at
          the column edge rather than paint across the promise, and a `nowrap`
          row would otherwise overhang in silence.
        */}
        <Box
          sx={{
            gridColumn: 1,
            gridRow: 1,
            minWidth: 0,
            overflow: 'hidden',
            justifySelf: 'start',
            display: { xs: 'none', lg: 'flex' },
            alignItems: 'center',
          }}
        >
          {showUtilities && <RibbonPhoneLinks />}
        </Box>

        <Box sx={{ gridColumn: 2, gridRow: 1, minWidth: 0, justifySelf: 'center' }}>
          <ImportPromiseRibbonMessage playSheen={play} />
        </Box>

        <Stack
          direction="row"
          sx={{ gridColumn: 3, gridRow: 1, alignItems: 'center', justifySelf: 'end' }}
        >
          {/*
            The handed-over copy: md and up only, and only once the utility
            strip has folded away. Below md that strip never exists, so there
            is nothing to hand over and nothing to be in two places at once —
            the row further down owns those widths outright.
          */}
          {showUtilities && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
              <RibbonSocialLinks />
            </Box>
          )}

          {/*
            The seam between the icon set and the control.

            Five brand glyphs ending in a sixth 16px glyph at 4px spacing is
            six social icons as far as the eye is concerned, and the sixth one
            closes the bar. Three things separate them, because at this size
            no one of them is enough on its own: a rule says the two groups
            are different kinds of thing, 12px either side says they are not
            one row, and the disc behind the cross below says the last one is
            a control. Only the rule is conditional — with the icons away
            there is nothing to be divided from, and a rule floating beside a
            lone cross is just a mark on the bar.
          */}
          {showUtilities && (
            <Box
              aria-hidden
              sx={{
                display: { xs: 'none', md: 'block' },
                flexShrink: 0,
                width: '1px',
                height: 16,
                mx: 3,
                // Translucent white rather than `divider`, which is a palette
                // token mixed for paper surfaces and reads as a scratch on
                // brand red. 0.28 is the weight at which the rule separates
                // without competing with the glyphs either side of it.
                bgcolor: 'rgba(255,255,255,0.28)',
              }}
            />
          )}

          <IconButton
            onClick={dismiss}
            aria-label={banner.dismiss}
            size="small"
            // 28px, not the theme's 44px: the row is 40px tall. The same
            // exception the social icons in the strip below already make, and
            // for the same reason — this is a dismissal, not a destination, and
            // the whole rest of the bar is the thing worth hitting.
            //
            // The disc is permanent rather than a hover state, and it is the
            // part that stops the cross reading as a sixth social icon: the
            // icons beside it are bare glyphs that *gain* a background on
            // hover, so a control that already has one is visibly not one of
            // them even before the pointer arrives. The same translucent
            // white the social row below md uses to group itself, at the
            // weight that keeps the cross itself above 3:1 on brand red.
            sx={{
              flexShrink: 0,
              minWidth: 0,
              minHeight: 0,
              p: 1.5,
              borderRadius: '50%',
              color: 'inherit',
              bgcolor: 'rgba(255,255,255,0.14)',
              opacity: 0.9,
              transition: (t) =>
                t.transitions.create(['opacity', 'background-color'], {
                  duration: t.transitions.duration.shorter,
                }),
              '&:hover, &:focus-visible': {
                opacity: 1,
                bgcolor: 'rgba(255,255,255,0.28)',
              },
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          >
            <CloseRounded sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>

        {/*
          The resident copy: below md, always, and on a row of its own.

          Not gated on `showSocials`, and that is the whole difference between
          the two breakpoints rather than an oversight. From md up the icons
          are a single set that moves between the strip and the control
          cluster, so one of the two has to be told when to hold them. Below
          md there is no strip to move from, so there is nothing to
          co-ordinate — the row is simply where they live.

          Spanning all three columns because the grid above it exists to keep
          the message optically centred against the controls, and this row has
          neither. The cluster carries its own translucent panel so the icons
          read as one object sitting on the bar rather than as a second line
          of chrome, which is what a full-width divider would have made it.
        */}
        <Box
          sx={{
            gridColumn: '1 / -1',
            gridRow: 2,
            display: { xs: 'flex', md: 'none' },
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 1,
              borderRadius: 999,
              // Lifted off the red rather than drawn on it: a border at this
              // size competes with the glyphs it is meant to group.
              bgcolor: 'rgba(255,255,255,0.12)',
            }}
          >
            <RibbonSocialLinks iconSize={18} pad={1.5} />
          </Box>
        </Box>
      </Box>
    </>
  );
}
